<template>
  <div class="pb-6 px-12">
    <v-skeleton-loader
      v-if="user"
      :loading="userDetailsLoading"
      type="list-item, divider, list-item-avatar, list-item-three-line@4, list-item-avatar-two-line@4"
      class="skeleton-loader--table"
    >
      <!-- Header -->
      <div class="d-flex align-center justify-space-between pb-4 pt-4 details-preview-header">
        <div class="d-flex flex-column">
          <span class="body2 text-indigo-medium" :data-testid="`${testIdPrefix}-protected-since`">
            {{ $t("usersPage.protectedSince") }}
            {{ getFormattedDateTime(userDetails?.protectionGrantedTime, "ll") }}
          </span>
          <div v-if="errorsList.length > 0" class="body2 d-flex align-center text-red-dark mt-1">
            <v-icon icon="$warning" class="mr-1" />
            <div v-for="(error, index) in errorsList" :key="error">
              <span>{{ $t(`mspUsersPage.statuses.${error}`) }}</span>
              <span v-if="index < errorsList.length - 1">,&nbsp;</span>
            </div>
          </div>
        </div>
        <v-menu v-if="actionsList.length" offset-y location="bottom right">
          <template #activator="{ props: activatorProps }">
            <v-btn
              class="ml-8"
              rounded
              color="primary"
              data-testid="user-preview-action-button"
              v-bind="activatorProps"
            >
              {{ $t("general.actions") }}
              <v-icon icon="$triangle" />
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="action in actionsList"
              :key="action"
              :data-testid="`${testIdPrefix}-${kebabCase(action)}-action`"
              @click="onActionClick(action)"
            >
              <v-list-item-title>{{ $t(`usersPage.actions.${action}`) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- User Info -->
      <div class="d-flex align-center mt-5 mb-7">
        <v-icon class="user-icon mr-3" icon="$user" />
        <span data-testid="users-preview-name" class="headline5">
          {{ userDetails?.name || user?.email }}
        </span>
      </div>

      <!-- Details Row -->
      <v-row :data-testid="`${testIdPrefix}-general-info`">
        <v-col cols="6">
          <!-- Workspace Info -->
          <div v-if="showWorkspaceInfo" class="mb-4">
            <div class="subtitle1">{{ $t("general.workspace") }}</div>
            <div class="body1" data-testid="msp-users-preview-workspace-id">
              {{ usersWorkspace?.id }}
            </div>
            <div
              class="body1 coro-link mb-4"
              data-testid="msp-users-preview-workspace-name"
              @click="onWorkspaceNameClick()"
            >
              {{ usersWorkspace?.name }}
            </div>
          </div>
          <!-- Emails & Aliases -->
          <div class="subtitle1">{{ $t("general.emails") }}</div>
          <div class="body2" :data-testid="`${testIdPrefix}-email`">{{ user?.email }}</div>
          <template v-if="displayShowMoreButton">
            <a
              v-if="!showAliases"
              class="coro-link body2"
              @click="showAliases = true"
              :data-testid="`${testIdPrefix}-aliases-trigger`"
            >
              {{
                `${$t("general.seeNMore", { n: aliases.length })} ${$t("general.aliases").toLowerCase()}`
              }}
            </a>
            <template v-if="showAliases">
              <span
                v-for="(alias, index) in aliases"
                :key="alias"
                class="body2 text-indigo-medium"
                :data-testid="`${testIdPrefix}-alias`"
              >
                <span v-if="index > 0">; </span>
                <span>{{ alias }}</span>
              </span>
              <br />
              <a
                class="coro-link body2 mt-1"
                @click="showAliases = false"
                :data-testid="`${testIdPrefix}-aliases-close-trigger`"
              >
                {{ $t("general.seeLess") }}
              </a>
            </template>
          </template>
          <!-- Delete email auto-forwarding rules -->
          <template v-if="showAutoForwardingBlockingStatus">
            <div class="subtitle1 mt-4 mb-2" data-testid="users-preview-auto-forwarding-header">
              {{ $t("usersPage.autoForwardingBlockingTitle") }}
            </div>
            <v-chip
              variant="flat"
              label
              color="green-faint"
              rounded="3"
              density="compact"
              data-testid="users-preview-auto-forwarding-status"
            >
              <span class="coro-label text-uppercase text-indigo-medium'">
                {{ $t("general.enabled") }}
              </span>
            </v-chip>
          </template>
        </v-col>
        <v-col cols="6">
          <!-- Protected Cloud Apps -->
          <div class="subtitle1">{{ $t("usersPage.protectedCloudApps") }}</div>
          <template v-if="cloudServices.length">
            <div
              v-for="service in cloudServices"
              :key="service.serviceName"
              class="d-flex align-center mb-4"
            >
              <coro-icon class="service-icon mr-2" :icon-name="service.serviceName"></coro-icon>
              <div class="subtitle1">{{ $t(`services.${service.serviceName}`) }}</div>
              <span
                class="coro-label text-uppercase text-primary text-no-wrap pt-1 pb-1 pr-2 pl-2 ml-2"
                v-if="service.mfaEnabled !== null && service.mfaEnabled !== undefined"
                :class="{
                  'bg-red-faint': !service.mfaEnabled,
                  'bg-green-faint': service.mfaEnabled,
                }"
                :data-testid="`${testIdPrefix}-service-${service.serviceName}-mfa`"
                >{{
                  service.mfaEnabled ? $t("usersPage.mfaEnabled") : $t("usersPage.mfaDisabled")
                }}</span
              >
            </div>
          </template>
          <span v-else class="subtitle1 text-indigo-medium">{{
            $t("usersPage.noProtectedCloudApps")
          }}</span>

          <!-- Labels -->
          <template v-if="config.context === AppContext.SINGLE_WORKSPACE && labels.length">
            <div class="subtitle1 mt-4 mb-3" data-testid="users-preview-labels-header">
              {{ $t("deviceDetails.labels") }}
            </div>
            <div data-testid="user-preview-labels">
              <v-chip
                v-for="label in labels"
                :key="label.id"
                variant="flat"
                label
                :color="label.color"
                rounded="3"
                density="compact"
                class="mr-1 mb-1"
              >
                <div class="d-flex align-center justify-space-between">
                  <span class="coro-label text-uppercase text-primary mr-1">{{
                    label.name.replace(/ /g, "&nbsp;")
                  }}</span>
                  <v-icon
                    v-if="!actionNotAllowed"
                    size="16"
                    @click="removeUserLabel(label)"
                    icon="$closeCircle"
                  />
                </div>
              </v-chip>
            </div>
          </template>
        </v-col>
      </v-row>

      <!-- Tickets, Devices, Activity Logs -->
      <user-associated-devices-table
        v-if="userDetails?.associatedDevices.length"
        :devices="userDetails.associatedDevices"
        @device-clicked="onAssociatedDeviceClick($event)"
      ></user-associated-devices-table>
      <entity-tickets-list
        class="mb-5 mt-3"
        :show-info-tooltip="restrictedTicketModules.length > 0"
        :access-restricted="ticketsAccessRestricted"
        :info-tooltip-text="modulesTooltipText"
        :tickets="tickets"
        :all-open-tickets-link="allOpenTicketsLink"
        :show-resolve="false"
        @view-vulnerability="viewVulnerability($event)"
      />
      <entity-activity-logs
        :hide-all-activity-link="config.context === AppContext.MSP"
        :access-restricted="activityLogsAccessRestricted"
        :activity-logs="activityLogs"
        :entity-id="user?.email"
        search-prefix="user"
        :loading="false"
        :show-load-more="config.context === AppContext.MSP"
        @undo-clicked="undoActivityLog($event)"
        @load-more-items-clicked="loadMoreActivityLogs()"
      />
    </v-skeleton-loader>
  </div>
</template>

<script setup lang="ts" generic="T extends PreviewUser">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import {
  type UserAssociatedDevice,
  type UserDetails,
  type UserListItem,
} from "@/_store/users.module.ts";
import { type MspUserListItem } from "@/_store/msp/msp-users.module.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import type { UserLabel } from "@/_store/users-settings/user-labels.module.ts";
import { getFormattedDateTime } from "@/_helpers/date";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { ModalWidth } from "@/constants/modals.ts";
import { RouteName } from "@/constants/routes.ts";
import { ProtectedUsersAction } from "@/constants/users.ts";
import { TicketStatus } from "@/constants/tickets.ts";
import { AppContext } from "@/types.ts";
import type {
  PreviewUser,
  UserPreviewConfig,
} from "@/components/user-preview/users-preview.config.ts"; // Import the new ConfigFor
import EntityTicketsList from "@/components/EntityTicketsList.vue";
import BulkTicketCloseModal from "@/components/modals/BulkTicketCloseModal.vue";
import AddOrRemoveLabelModal from "@/components/modals/AddOrRemoveLabelModal.vue";
import CoroIcon from "@/components/CoroIcon.vue";
import EntityActivityLogs from "@/components/EntityActivityLogs.vue";
import kebabCase from "lodash/kebabCase";
import UserAssociatedDevicesTable from "@/components/UserAssociatedDevicesTable.vue";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

const props = withDefaults(
  defineProps<{
    user: T | null;
    config: UserPreviewConfig<T>;
    availableLabels?: UserLabel[];
  }>(),
  {
    availableLabels: () => [],
  }
);

const dialogStore = useDialogsStore();
const i18n = useI18n();
const router = useRouter();

const showAliases = ref(false);
const actionsList = ref<ProtectedUsersAction[]>([]);
const userDetails = computed(() => props.config.userDetails.value);
const userDetailsLoading = computed(() => props.config.userDetailsLoading.value);
const aliases = computed(() => userDetails.value?.aliases ?? []);
const cloudServices = computed(() => userDetails.value?.cloudServices ?? []);
const tickets = computed(() => userDetails.value?.tickets ?? []);
const activityLogs = computed(() => userDetails.value?.activityLogs ?? []);
const labels = computed<UserLabel[]>(() =>
  props.config.context === AppContext.SINGLE_WORKSPACE
    ? ((userDetails.value as UserDetails)?.labels ?? [])
    : []
);
const showAutoForwardingBlockingStatus = computed(
  () =>
    props.config.context === AppContext.SINGLE_WORKSPACE &&
    userDetails.value?.autoForwardingBlockingPolicySelected
);
const displayShowMoreButton = computed(() => aliases.value.length > 0);
const testIdPrefix = computed(() => props.config.testIdPrefix);

const restrictedTicketModules = computed(() =>
  (
    [
      SubscriptionModule.CLOUD_SECURITY,
      SubscriptionModule.USER_DATA_GOVERNANCE,
      SubscriptionModule.EMAIL_SECURITY,
    ] as const
  ).filter((module) => props.config.isAccessRestricted(RolePermissionsScope.TICKETS, module))
);

const ticketsAccessRestricted = computed(() => restrictedTicketModules.value.length === 3);

const activityLogsAccessRestricted = computed(() =>
  props.config.isAccessRestricted(
    RolePermissionsScope.WORKSPACE_MANAGEMENT,
    WorkspaceManagementScopeSections.ACTIVITY_LOGS
  )
);

const modulesTooltipText = computed(() =>
  i18n.t(
    "entityTicketsList.modulesTooltip",
    {
      modules: restrictedTicketModules.value
        .map((module) => i18n.t(`subscriptions.modules.${module}`))
        .join(","),
    },
    restrictedTicketModules.value.length
  )
);

const actionNotAllowed = computed(() =>
  props.config.isActionRestricted(RolePermissionsScope.VIEWS, "usersView")
);

const usersWorkspace = computed(() => {
  if (props.config.context === AppContext.SINGLE_WORKSPACE || !props.user) return null;
  const mspUser = props.user as MspUserListItem;
  return { id: mspUser.workspaceId, name: mspUser.workspaceName };
});

const showWorkspaceInfo = computed(() => {
  return props.config.context === AppContext.MSP && usersWorkspace.value;
});

const errorsList = computed(() => {
  const { suspended } = userDetails.value ?? { suspended: false };
  const unresolvedTickets = tickets.value.length > 0;
  const errors: Record<string, boolean> = { suspended, unresolvedTickets };
  return Object.keys(errors).filter((key) => errors[key]);
});

const allOpenTicketsLink = computed(() => {
  if (!props.user) return {};
  return {
    name: props.config.ticketsPageRoute,
    query: {
      search: `violator:${props.user?.email}`,
      eventTriggers: userDetails.value?.tickets.map((v) => v.name).join(","),
      status: TicketStatus.OPEN,
      protectedUsersOnly: "true",
    },
  };
});

const onAssociatedDeviceClick = (device: UserAssociatedDevice) => {
  const route =
    props.config.context === AppContext.SINGLE_WORKSPACE
      ? RouteName.DEVICES
      : RouteName.MSP_DEVICES_TAB;

  router.push({ name: route, query: { search: device.enrollmentCode } });
};

const viewVulnerability = (vulnerability: string) => {
  if (!props.user) return;
  const mspUser = props.user as MspUserListItem;
  router.push({
    name: props.config.ticketsPageRoute,
    query: {
      eventTriggers: vulnerability,
      search: `violator:${props.user?.email}`,
      protectedUsersOnly: "true",
      ...(mspUser.workspaceId ? { childWorkspaceIds: mspUser.workspaceId } : {}),
    },
  });
};

const handleMarkAsProcessed = async () => {
  if (!props.user) return;
  await props.config.applySingleUserAction(props.user.id, {
    action: ProtectedUsersAction.MARK_AS_PROCESSED,
  });
};

const onActionClick = (action: ProtectedUsersAction) => {
  if (!props.user) return;
  const disable = actionNotAllowed.value;

  switch (action) {
    case ProtectedUsersAction.MARK_AS_PROCESSED:
      const selection =
        props.config.context === AppContext.MSP
          ? [props.user as MspUserListItem]
          : { isAllPages: false, include: [props.user as UserListItem], exclude: [] };

      dialogStore.openDialog(
        componentDialogsConfigConstructor({
          item: {
            selection,
            callback: handleMarkAsProcessed,
            modules: [
              SubscriptionModule.EMAIL_SECURITY,
              SubscriptionModule.CLOUD_SECURITY,
              SubscriptionModule.USER_DATA_GOVERNANCE,
            ],
          },
          action,
          component: BulkTicketCloseModal,
          width: ModalWidth.MEDIUM,
          callback: () => {},
          hideFooter: true,
          disable,
        })
      );
      break;
    case ProtectedUsersAction.ADD_LABEL:
      dialogStore.openDialog({
        ...componentDialogsConfigConstructor({
          item: {
            ...(userDetails.value ?? {}),
            labelsLink: { name: RouteName.USERS_SETTINGS_LABELS_TAB },
            availableLabels: props.availableLabels,
          },
          action,
          component: AddOrRemoveLabelModal,
          width: ModalWidth.SMALL,
          callback: async (data: { item: { metadata: { labels: UserLabel[] } } }) => {
            await props.config.applySingleUserAction(props.user!.id, {
              action,
              metadata: { ...data.item.metadata },
            });
            await props.config.getUserDetails(props.user!);
          },
          disable,
        }),
        header: { title: i18n.t(`usersPage.actions.${action}`), close: true },
      });
      break;
    default:
      dialogStore.openDialog(
        confirmationDialogsConfigConstructor({
          item: props.user,
          action,
          disable,
          callback: () => {
            if (props.user) {
              return props.config.applySingleUserAction(props.user.id, { action });
            }
          },
        })
      );
      break;
  }
};

const removeUserLabel = async (label: UserLabel) => {
  if (!props.user) return;
  await props.config.applySingleUserAction(props.user.id, {
    action: ProtectedUsersAction.REMOVE_LABEL,
    metadata: { labels: [label.id] },
  });
  await props.config.getUserDetails(props.user);
};

const fetchUserActions = async (user: PreviewUser) => {
  try {
    const { data } = await props.config.getSingleUserActions(user.id);
    actionsList.value = data.items;
  } catch {
    actionsList.value = [];
  }
};

const loadMoreActivityLogs = () => props.config.loadMoreMspUserActivityLogs?.();

const undoActivityLog = (activityLogId: string) => props.config.undoActivityLog(activityLogId);

const onWorkspaceNameClick = async () => {
  if (!usersWorkspace.value) return;
  const workspaceStore = useWorkspaceStore();
  await workspaceStore.updateWorkspaceAndPermissions(usersWorkspace.value.id, false);
  await useAccountStore().checkMspAccess();
  await router.push({ name: RouteName.DASHBOARD_PAGE });
};

watch(
  () => props.user?.id,
  async (userId) => {
    showAliases.value = false;
    if (!userId || !props.user) {
      actionsList.value = [];
      return;
    }
    await props.config.getUserDetails(props.user);
    await fetchUserActions(props.user);
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
:deep(*) {
  .service-icon {
    width: 40px;
    height: 40px;
  }
  .user-icon {
    font-size: 40px;
  }
  .table-container {
    border: 1px solid rgb(var(--v-theme-indigo-faint));
  }
}
</style>
